import type { EquippedMap, ItemSetId, LegendaryEffect } from './types';
import { getItem } from './items';

export type SetBonusEffect =
  | 'atk_pct'           // % ATK bonus (multiplicative after base) — stacks additively across multiple grants
  | 'def_pct'           // % DEF bonus (multiplicative after base)
  | 'hp_pct'            // % HP bonus
  | 'final_hour_dr'     // 3pc capstone: at <30% HP, 50% damage reduction for 5s, 1×/run
  | 'second_wind'       // 6pc capstone: revive once with 50% HP on death, 1×/run
  // ── Legacy effect kinds — kept for type-compatibility with existing call sites in
  // dungeonStore.ts / gameStore.ts. After the 2026-05 catalog cleanup no item grants
  // these any more, so hasSetBonus() / getSetBonusValue() always return false / 0.
  // Call sites are deliberately left in place so a future set can re-introduce the effect.
  | 'execute_reset'
  | 'reflect_dmg'
  | 'terminal_tick'
  | 'market_premium'
  | 'flat_drop_chance'
  | 'boss_uptier';

export interface SetBonusDef {
  setId: ItemSetId;
  /** Number of pieces required. Hollow-Knight has 8 set-tagged items, so
   *  breakpoints span 2 / 3 / 4 / 5 / 6 (no 7/8 capstone yet). */
  pieces: 2 | 3 | 4 | 5 | 6;
  effect: SetBonusEffect;
  value: number;
}

export const SET_BONUSES: SetBonusDef[] = [
  // Hollow Knight (8 pieces: Armor + Mask + Cloak + Scarf + Drape + Heart + Nail + Shield)
  // Theme: void/soul magic — survivability + damage scaling.
  { setId: 'hollow_knight', pieces: 2, effect: 'hp_pct',        value: 0.20 },
  { setId: 'hollow_knight', pieces: 3, effect: 'def_pct',       value: 0.18 },
  { setId: 'hollow_knight', pieces: 3, effect: 'final_hour_dr', value: 0.50 },
  { setId: 'hollow_knight', pieces: 4, effect: 'atk_pct',       value: 0.20 },
  { setId: 'hollow_knight', pieces: 5, effect: 'hp_pct',        value: 0.15 },
  { setId: 'hollow_knight', pieces: 6, effect: 'second_wind',   value: 0.50 },

  // Vault Knight (8 pieces: Armor + Helm + Pauldrons + Sash + Belt + Relic + Blade + Buckler)
  // Theme: storm/lightning — high attack scaling, fast hit cadence, tankier
  // 6pc capstone than HK to compensate for the missing 3pc final-hour DR.
  // Synergizes with Stormcaller aspect (atk-heavy, special-cast burst).
  { setId: 'vault_knight', pieces: 2, effect: 'atk_pct',     value: 0.25 },
  { setId: 'vault_knight', pieces: 3, effect: 'atk_pct',     value: 0.15 },
  { setId: 'vault_knight', pieces: 4, effect: 'hp_pct',      value: 0.20 },
  { setId: 'vault_knight', pieces: 5, effect: 'def_pct',     value: 0.20 },
  { setId: 'vault_knight', pieces: 6, effect: 'second_wind', value: 0.60 },
];

/** Count how many pieces of each set are currently equipped. */
function countSetPieces(equipment: EquippedMap): Partial<Record<ItemSetId, number>> {
  const counts: Partial<Record<ItemSetId, number>> = {};
  for (const inst of Object.values(equipment)) {
    if (!inst) continue;
    const def = getItem(inst.itemId);
    if (def?.setId) counts[def.setId] = (counts[def.setId] ?? 0) + 1;
  }
  return counts;
}

/** Returns all active set bonuses for the current equipment loadout. */
export function getActiveSetBonuses(equipment: EquippedMap): SetBonusDef[] {
  const counts = countSetPieces(equipment);
  return SET_BONUSES.filter((b) => (counts[b.setId] ?? 0) >= b.pieces);
}

/** Check if a specific set bonus effect is active. */
export function hasSetBonus(equipment: EquippedMap, effect: SetBonusEffect): boolean {
  return getActiveSetBonuses(equipment).some((b) => b.effect === effect);
}

/** Get the value of a set bonus (0 if not active). Multiple grants with the
 *  same effect (e.g. 3pc atk_pct + 5pc atk_pct) sum together. */
export function getSetBonusValue(equipment: EquippedMap, effect: SetBonusEffect): number {
  return getActiveSetBonuses(equipment)
    .filter((b) => b.effect === effect)
    .reduce((sum, b) => sum + b.value, 0);
}

/** Get set piece counts for display in UI. */
export function getSetPieceCounts(equipment: EquippedMap): Partial<Record<ItemSetId, number>> {
  return countSetPieces(equipment);
}

/** Returns the set-id if the full set is equipped (6 pieces is the highest
 *  breakpoint), else null. */
export function getFullEquippedSet(equipment: EquippedMap): ItemSetId | null {
  const counts = countSetPieces(equipment);
  for (const [setId, count] of Object.entries(counts)) {
    if (count >= 6) return setId as ItemSetId;
  }
  return null;
}

/** Check if a legendary effect is equipped in any slot. */
export function hasLegendaryEffect(equipment: EquippedMap, effect: LegendaryEffect): boolean {
  for (const inst of Object.values(equipment)) {
    if (!inst) continue;
    const def = getItem(inst.itemId);
    if (def?.legendaryEffect === effect) return true;
  }
  return false;
}

export const SET_NAMES: Record<ItemSetId, string> = {
  hollow_knight: 'Hohler Ritter',
  vault_knight: 'Vault-Ritter',
};
